import React, { useEffect, useState } from 'react';
import { Grid, Link, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { Event, EventStatus, EventType } from '../../../services/Types';
import { convertMinutesIntoTime } from '../../../services/Helper';
import moment from 'moment';
import { useEvent } from '../../../hooks/useEvent';
import { BadgeEventTitle } from '../../../components/BadgeEventTitle/BadgeEventTitle';
import { BadgeStatus } from '../../../components/BadgeStatus/BadgeStatus';
import { usePermissions } from '../../../hooks/usePermissions';

interface DateAndTimesReviewProps {
  eventData: Event | undefined;
  handleEventChange: (field: keyof Event) => (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DateAndTimesReview: React.FC<DateAndTimesReviewProps> = ({ 
  eventData, 
  handleEventChange 
}) => {
  const { getAllSeriesEvents } = useEvent()
  const [seriesEvents, setSeriesEvents] = useState<Event[]>([])
  const { eventIsUnlocked } = usePermissions(eventData || {} as Event);
  useEffect(() => {
    if (eventData?.id) {
      getAllSeriesEvents(eventData.id).then((events) => {
        setSeriesEvents(events)
      })
    }
  }, [eventData?.id])

  const handleRoomBookingChange = (index: number, field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!eventData?.roomBooking) return;
    
    const updatedBookings = eventData.roomBooking.map((booking, i) => 
      i === index ? { ...booking, [field]: e.target.value } : booking
    );

    const syntheticEvent = {target: {value: updatedBookings}} as unknown as React.ChangeEvent<HTMLInputElement>;

    handleEventChange('roomBooking')(syntheticEvent);
  };

  let liveEvent = eventData?.roomBooking?.find(booking => booking.category === 'live');
  const dryEvent = eventData?.roomBooking?.find(booking => booking.category === 'dry');
  const blockedEvent = eventData?.roomBooking?.find(booking => booking.category === null);

  if (blockedEvent && eventData?.status === EventStatus.BLOCKED) {
    liveEvent = blockedEvent;
  }

  //styles
  const styles = {
    container: {
      border: '1px solid #e0e0e0',
      padding: 2,
      borderRadius: 2,
      margin: 0,
      marginBottom: 2,
      backgroundColor: '#f5f5f5'
    }
  }
  
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        { eventData?.roomBooking?.map((booking, index) => {
            return (
              <Grid container spacing={2} sx={styles.container}>
                <Grid item xs={6}>
                  <TextField
                    label={booking.category === 'live' ? 'Live event date' : 'Dry run event date'}
                    value={moment.utc(Number(booking?.date)).format('MMMM DD, YYYY') || ''}
                    onChange={handleRoomBookingChange(0, 'date')}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={booking.category === 'live' ? 'Live event time' : 'Dry run event time'}
                    value={(convertMinutesIntoTime(booking?.startTime || 0) || '') + ' - ' + (convertMinutesIntoTime(booking?.endTime || 0) || '')}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            )
          })
        }
        { eventData?.publicationDate && (
          <Grid container spacing={2} sx={styles.container}>
            <Grid item xs={6}>
              <TextField
                label='Publication date'
                value={moment.utc(Number(eventData?.publicationDate)).format('MMMM DD, YYYY') || ''}
                onChange={handleEventChange('publicationDate')}
                disabled={true}
              />
            </Grid>
          </Grid>
        )}
        { eventData?.eventType === EventType.SERIES && (
          <Grid item xs={12}>
            <Typography variant="h6">Series events</Typography>
            <List>
              { seriesEvents.map((event, index) => {
                if (event.eventType === EventType.SERIES) return null;

                let liveEvent = event?.roomBooking?.find(booking => booking.category === 'live');
                const dryEvent = event?.roomBooking?.find(booking => booking.category === 'dry');
                const blockedEvent = event?.roomBooking?.find(booking => booking.category === null);

                if (blockedEvent && event?.status === EventStatus.BLOCKED) {
                  liveEvent = blockedEvent;
                }

                return (
                <ListItemButton key={index} component={Link}
                  href={`/event/review/${event.id}`}
                  target="_blank" rel="noopener noreferrer"
                  sx={{
                    width: '50%',
                    padding: 1,
                    margin: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#e0e0e0'
                    }
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <BadgeEventTitle eventType={event.eventType} eventTitle={event.title} />
                      <BadgeStatus status={event.status} />
                    </Grid>
                    <Grid item xs={4}>
                      <ListItemText primary='Live event'
                        secondary={moment.utc(Number(liveEvent?.date)).format('MMMM DD, YYYY') || ''}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      {event.eventType === EventType.WEBINAR ? (
                        <ListItemText primary='Dry run event'
                          secondary={moment.utc(Number(dryEvent?.date)).format('MMMM DD, YYYY') || ''}
                        />
                      ) : (
                        <ListItemText primary='Publication date'
                          secondary={moment.utc(Number(event.publicationDate)).format('MMMM DD, YYYY') || ''}
                        />
                      )}
                    </Grid>
                  </Grid>
                </ListItemButton>
              )})}
            </List>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}; 